import { useRouterState } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  /** Uložená preference (light/dark/system) — ovládá ji jen dashboard přepínač. */
  theme: Theme;
  /** Skutečně aplikovaný režim po zohlednění routy. Veřejný web = vždy "light". */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function prefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Veřejné a auth stránky se renderují VŽDY tmavě (brand design webu).
 * Light/dark přepínač platí výhradně pro /dashboard, aby přepnutí
 * v administraci nikdy neovlivnilo vzhled veřejného webu.
 */
function isThemedRoute(pathname: string): boolean {
  return pathname.startsWith("/dashboard");
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("theme") as Theme | null) ?? "dark";
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("dark");

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    // Veřejný/auth web = vždy dark. Dashboard ctí uloženou preferenci.
    const wantDark = isThemedRoute(pathname)
      ? theme === "dark" || (theme === "system" && prefersDark())
      : true;
    document.documentElement.classList.toggle("dark", wantDark);
    setResolvedTheme(wantDark ? "dark" : "light");
  }, [theme, pathname]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
